/**
 * AdminChatView — /admin/chat/:sessionId
 *
 * Allows admin/superadmin to:
 *   1. Read the full user conversation
 *   2. Type a reply that Claude Haiku will reformat and send to the user
 *   3. Approve the resulting KB update before it's written to chatbot_kb.md
 *
 * Accessible only to admin and superadmin roles.
 * Telegram escalation links point here.
 */

import React, { useState, useRef, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Spinner } from "reactstrap";

// Runtime white-label config — set by nginx-mounted /app-config.js
const appName = (window.APP_CONFIG || {}).appName || "Fincrest Systems";

export default function AdminChatView() {
  const { sessionId }    = useParams();
  const { user }         = useAuth();
  const history          = useHistory();
  const [session, setSession]         = useState(null);
  const [sessionMsgs, setSessionMsgs] = useState([]);
  const [sessionCost, setSessionCost] = useState(null);
  const [loadingSession, setLoadingSession] = useState(true);
  const [reply, setReply]             = useState("");
  const [sending, setSending]         = useState(false);
  const [kbDraft, setKbDraft]         = useState(null);
  const [kbApproved, setKbApproved]   = useState(false);
  const [sent, setSent]               = useState(false);
  const [error, setError]             = useState(null);
  const bottomRef = useRef(null);

  const isAdmin = user?.user_type === "admin" || user?.user_type === "superadmin";

  /* fetch session info + messages */
  useEffect(() => {
    if (!sessionId || !isAdmin) return;
    setLoadingSession(true);
    Promise.all([
      axios.get("/chat/admin/sessions").catch(() => ({ data: [] })),
      axios.get(`/chat/session/${sessionId}/messages`).catch(() => ({ data: [] })),
      axios.get(`/chat/session/${sessionId}/cost`).catch(() => ({ data: { total_cost_usd: null } })),
    ]).then(([sessRes, msgsRes, costRes]) => {
      const found = (sessRes.data || []).find(s => s.id === sessionId);
      setSession(found || { id: sessionId, username: "—", escalated_question: "" });
      setSessionMsgs(msgsRes.data || []);
      setSessionCost(costRes.data?.total_cost_usd ?? null);
    }).finally(() => setLoadingSession(false));
  }, [sessionId, isAdmin]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [sessionMsgs]);

  if (!isAdmin) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#dc3545" }}>
        Access denied — admin only.
      </div>
    );
  }

  if (loadingSession) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#888" }}>
        <Spinner size="sm" /> Loading session…
      </div>
    );
  }

  const handleSendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    setError(null);
    try {
      const res = await axios.post("/chat/admin/reply", {
        session_id: sessionId,
        raw_answer: reply,
      });
      setKbDraft(res.data.kb_draft || null);
      setSent(true);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to send reply. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleApproveKb = async () => {
    try {
      await axios.post("/chat/admin/approve-kb", {
        session_id: sessionId,
        kb_entry:   kbDraft,
      });
      setKbApproved(true);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to update knowledge base.");
    }
  };

  return (
    <div style={{
      maxWidth: 900,
      margin: "0 auto",
      padding: "24px 20px",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    }}>
      {/* ── breadcrumb ── */}
      <div style={{ fontSize: 12, color: "#aaa", marginBottom: 16 }}>
        <span
          style={{ cursor: "pointer", color: "#667eea" }}
          onClick={() => history.push("/admin/users")}
        >
          Admin
        </span>
        {" / "}
        <span
          style={{ cursor: "pointer", color: "#667eea" }}
          onClick={() => history.push("/chat")}
        >
          Support Chats
        </span>
        {" / "}
        Session {sessionId}
      </div>

      {/* ── session header ── */}
      <div style={{
        background: "#fff", borderRadius: 12,
        border: "1px solid #eee", padding: "16px 20px",
        marginBottom: 20,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: "#333" }}>
            Chat session: {session?.username || "—"}
          </div>
          <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
            Session ID: {sessionId}  ·  {sessionMsgs.length} messages
            {sessionCost != null && sessionCost > 0 && (
              <span style={{ marginLeft: 8, color: "#e67e22", fontWeight: 600 }}>
                · API cost: ${sessionCost.toFixed(6)}
              </span>
            )}
          </div>
        </div>
        {session?.status === "escalated" && (
          <span style={{
            background: "#fff3cd", color: "#856404",
            border: "1px solid #ffc107",
            borderRadius: 20, padding: "4px 12px",
            fontSize: 12, fontWeight: 600,
          }}>
            ⚠ Awaiting admin reply
          </span>
        )}
      </div>

      {/* ── escalated question highlight ── */}
      <div style={{
        background: "#fff8e1", border: "1px solid #ffe082",
        borderRadius: 10, padding: "14px 18px", marginBottom: 20,
      }}>
        <div style={{ fontSize: 11, color: "#8a6914", fontWeight: 700, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>
          Escalated question
        </div>
        <div style={{ fontSize: 14, color: "#333", lineHeight: 1.6 }}>
          {session?.escalated_question
            ? `"${session.escalated_question}"`
            : <em style={{ color: "#aaa" }}>No escalated question on record.</em>
          }
        </div>
      </div>

      {/* ── full conversation ── */}
      <div style={{
        background: "#fff", borderRadius: 12,
        border: "1px solid #eee",
        padding: "16px 20px",
        marginBottom: 20,
        maxHeight: 400, overflowY: "auto",
      }}>
        <div style={{ fontSize: 12, color: "#888", fontWeight: 600, marginBottom: 12 }}>
          Full conversation
        </div>
        {sessionMsgs.length === 0 && (
          <div style={{ color: "#aaa", fontSize: 13 }}>No messages found.</div>
        )}
        {sessionMsgs.map(msg => (
          <div key={msg.id} style={{
            display: "flex",
            flexDirection: "column",
            alignItems: msg.role === "user" ? "flex-end" : "flex-start",
            marginBottom: 12,
          }}>
            <div style={{
              maxWidth: "75%",
              padding: "9px 13px",
              borderRadius: msg.role === "user"
                ? "14px 14px 4px 14px"
                : "14px 14px 14px 4px",
              background: msg.role === "user"
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                : msg.escalated
                  ? "#fff8e1"
                  : "#f4f4f8",
              color: msg.role === "user" ? "#fff" : "#333",
              fontSize: 13,
              lineHeight: 1.5,
              border: msg.escalated ? "1px solid #ffe082" : "none",
            }}>
              <div style={{ fontSize: 10, color: msg.role === "user" ? "rgba(255,255,255,0.7)" : "#aaa", marginBottom: 3 }}>
                {msg.role === "user" ? (session?.username || "User") : `${appName} AI`}
                {" · "}{formatTime(msg.created_at)}
              </div>
              {msg.content}
            </div>
            {msg.role === "bot" && <MessageMeta msg={msg} />}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* ── error banner ── */}
      {error && (
        <div style={{
          background: "#f8d7da", border: "1px solid #f5c6cb",
          borderRadius: 10, padding: "12px 16px", marginBottom: 16,
          color: "#721c24", fontSize: 13,
        }}>
          ⚠ {error}
        </div>
      )}

      {/* ── admin reply panel ── */}
      {!sent && session?.status === "escalated" ? (
        <div style={{
          background: "#fff", borderRadius: 12,
          border: "1px solid #eee", padding: "20px",
          marginBottom: 20,
        }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: "#333", marginBottom: 12 }}>
            Your answer
          </div>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 10, lineHeight: 1.5 }}>
            Write your answer below. Claude Haiku will reformat and polish it before sending
            to the user. The original phrasing will be preserved in meaning.
          </div>
          <textarea
            value={reply}
            onChange={e => setReply(e.target.value)}
            placeholder="Type your answer here…"
            rows={5}
            style={{
              width: "100%", border: "1px solid #e0e0e0",
              borderRadius: 8, padding: "10px 12px",
              fontSize: 13, fontFamily: "inherit",
              resize: "vertical", outline: "none",
              boxSizing: "border-box",
            }}
            onFocus={e => e.target.style.borderColor = "#667eea"}
            onBlur={e => e.target.style.borderColor = "#e0e0e0"}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
            <button
              onClick={handleSendReply}
              disabled={!reply.trim() || sending}
              style={{
                background: (!reply.trim() || sending)
                  ? "#e0e0e0"
                  : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                border: "none", borderRadius: 8,
                color: "#fff", fontWeight: 600,
                padding: "10px 24px", cursor: (!reply.trim() || sending) ? "not-allowed" : "pointer",
                fontSize: 13, display: "flex", alignItems: "center", gap: 8,
              }}
            >
              {sending && <Spinner size="sm" style={{ width: 14, height: 14 }} />}
              {sending ? "Sending via Haiku…" : "Send reply to user"}
            </button>
          </div>
        </div>
      ) : (
        <div style={{
          background: "#d4edda", border: "1px solid #c3e6cb",
          borderRadius: 10, padding: "14px 18px", marginBottom: 20,
          color: "#155724", fontSize: 13,
        }}>
          ✓ Reply sent to user. They have been notified (in-app + Telegram if configured).
        </div>
      )}

      {/* ── KB update approval ── */}
      {kbDraft && !kbApproved && (
        <div style={{
          background: "#fff", borderRadius: 12,
          border: "1px solid #c3e6cb", padding: "20px",
        }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: "#333", marginBottom: 4 }}>
            📚 Knowledge Base Update
          </div>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 14, lineHeight: 1.5 }}>
            Claude Haiku has proposed adding this Q&A to <code>chatbot_kb.md</code> so the bot
            can answer similar questions automatically in future. Review and approve before it's written.
          </div>

          <div style={{ background: "#f8f9fa", borderRadius: 8, padding: "14px 16px", marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 6 }}>
              SECTION: {kbDraft.section}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#333", marginBottom: 6 }}>
              Q: {kbDraft.question}
            </div>
            <div style={{ fontSize: 13, color: "#555", lineHeight: 1.6 }}>
              A: {kbDraft.answer}
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button
              onClick={() => setKbDraft(null)}
              style={{
                background: "none", border: "1px solid #e0e0e0",
                borderRadius: 8, padding: "8px 18px",
                color: "#666", cursor: "pointer", fontSize: 13,
              }}
            >
              Reject
            </button>
            <button
              onClick={handleApproveKb}
              style={{
                background: "#28a745", border: "none",
                borderRadius: 8, padding: "8px 18px",
                color: "#fff", cursor: "pointer",
                fontSize: 13, fontWeight: 600,
              }}
            >
              ✓ Approve & write to KB
            </button>
          </div>
        </div>
      )}

      {kbApproved && (
        <div style={{
          background: "#d4edda", border: "1px solid #c3e6cb",
          borderRadius: 10, padding: "14px 18px",
          color: "#155724", fontSize: 13,
        }}>
          ✓ Knowledge base updated. The bot will use this answer for similar questions.
        </div>
      )}
    </div>
  );
}

function formatTime(ts) {
  if (!ts) return "";
  const d = ts instanceof Date ? ts : new Date(ts);
  return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}

function MessageMeta({ msg }) {
  if (!msg.model_used) return null;

  if (msg.model_used === "cache") {
    return (
      <div style={{ fontSize: 10, color: "#aaa", marginTop: 3, paddingLeft: 4 }}>
        Local DB
        {msg.cache_entry_id != null
          ? <span style={{ color: "#667eea", marginLeft: 4 }}>· KB #{msg.cache_entry_id}</span>
          : null}
      </div>
    );
  }

  const modelLabel = msg.model_used.includes("haiku")  ? "Haiku"
                   : msg.model_used.includes("sonnet") ? "Sonnet"
                   : msg.model_used;

  return (
    <div style={{ fontSize: 10, color: "#aaa", marginTop: 3, paddingLeft: 4 }}>
      Claude API · {modelLabel}
      {msg.cost_usd != null && msg.cost_usd > 0
        ? <span style={{ color: "#e67e22", marginLeft: 4 }}>· ${msg.cost_usd.toFixed(6)}</span>
        : null}
    </div>
  );
}
